// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import CardScanPanel from "../components/CardScanPanel";
import { getCollectionItems, saveCollectionItem } from "../api/cards";
import { canonicalGameToDisplayLabel } from "../utils/canonicalGames";
import { useToast } from "../components/ToastProvider";
import { getUser } from "../auth/auth";
import { preparePostTradeDraftFromCard } from "../utils/postTradeDraftPrefill";
import "../styles/Collection.css";

export default function Collection() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = getUser();
  const [items, setItems] = useState({ readyToSave: [], saved: [] });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [tradingId, setTradingId] = useState("");

  const loadCollection = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCollectionItems();
      setItems({
        readyToSave: Array.isArray(data?.readyToSave) ? data.readyToSave : [],
        saved: Array.isArray(data?.saved) ? data.saved : [],
      });
    } catch (error) {
      console.error("Failed to load collection", error);
      setItems({ readyToSave: [], saved: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCollection();
  }, [loadCollection]);

  const handleSaveReadyItem = async (id) => {
    setSavingId(id);
    try {
      await saveCollectionItem({ id });
      await loadCollection();
    } catch (error) {
      console.error("Failed to save collection item", error);
    } finally {
      setSavingId("");
    }
  };

  const handleTradeFromCollection = async (item) => {
    setTradingId(item.id);
    try {
      const result = await preparePostTradeDraftFromCard({
        userId: user?.id ?? null,
        sourceContext: "collection",
        confirmedCard: {
          cardId: item.cardId,
          cardName: item.cardNameCanonical || item.displayName || "",
          game: item.cardGame,
        },
        cardName: item.displayName || item.cardNameCanonical || "",
        condition: item.condition || "Near Mint",
        gradingCompany: item.gradingCompany || "raw",
        gradeNumber: item.gradeNumber || "",
        frontImageUrl: item.frontImageUrl || "",
        backImageUrl: item.backImageUrl || "",
        imageFileName: `${item.displayName || item.cardNameCanonical || "trade-image"}.webp`,
        blockOnImageFailure: true,
      });

      if (!result.ok) {
        showToast(result.error || "Could not prepare trade draft for this card.", "error");
        return;
      }

      navigate("/post-trade");
    } catch (error) {
      showToast(error?.message || "Could not prepare trade draft for this card.", "error");
    } finally {
      setTradingId("");
    }
  };

  return (
    <>
      <TopNav />
      <div className="collection-page page-with-top-nav">
        <div className="collection-hero">
          <div>
            <h1>My Collection</h1>
            <p>Scan cards, review AI suggestions, and build a verified personal inventory.</p>
          </div>
        </div>

        <section className="collection-scan-section">
          <div className="collection-scan-header">
            <h2>Scan Into Collection</h2>
            <p>Upload a front photo, review the scan result, then confirm it into your Ready to Save list.</p>
          </div>
          <CardScanPanel
            flowContext="collection"
            title="Collection Scanner"
            subtitle="Upload a front card image, then review the catalog match, quantity, condition, and grading before saving."
            readyToSaveItems={items.readyToSave}
            savedCollectionItems={items.saved}
            onCollectionChanged={loadCollection}
            onConfirmed={async () => {
              await loadCollection();
            }}
          />
        </section>

        <section className="collection-list-section">
          <div className="collection-list-header">
            <h2>Ready to Save</h2>
            <button type="button" className="btn-secondary" onClick={() => void loadCollection()}>
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="collection-empty">Loading collection...</div>
          ) : items.readyToSave.length === 0 ? (
            <div className="collection-empty">No staged cards yet. Confirm a scan to add one here.</div>
          ) : (
            <div className="collection-grid">
              {items.readyToSave.map((item) => (
                <article key={item.id} className="collection-card">
                  {item.frontImageUrl ? (
                    <img src={item.frontImageUrl} alt={item.displayName || item.cardNameCanonical} />
                  ) : (
                    <div className="collection-card-placeholder">No image</div>
                  )}
                  <div className="collection-card-body">
                    <h3>{item.displayName || item.cardNameCanonical}</h3>
                    <p className="collection-meta-strong">{`Quantity: ${item.quantity || 1}`}</p>
                    <p>{[canonicalGameToDisplayLabel(item.cardGame), item.condition].filter(Boolean).join(" • ") || "Unspecified"}</p>
                    {item.gradingCompany && item.gradingCompany !== "raw" && (
                      <p>{`${String(item.gradingCompany).toUpperCase()} ${item.gradeNumber || ""}`.trim()}</p>
                    )}
                    {item.scanAnalysis?.analysis?.authenticityRisk?.level && (
                      <span className="collection-risk-tag">
                        Authenticity: {item.scanAnalysis.analysis.authenticityRisk.level}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn-secondary collection-save-btn"
                      onClick={() => void handleSaveReadyItem(item.id)}
                      disabled={savingId === item.id}
                    >
                      {savingId === item.id ? "Saving..." : "Save to Collection"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="collection-list-section">
          <div className="collection-list-header">
            <h2>Saved Cards</h2>
          </div>

          {loading ? null : items.saved.length === 0 ? (
            <div className="collection-empty">No saved cards yet.</div>
          ) : (
            <div className="collection-grid">
              {items.saved.map((item) => (
                <article key={item.id} className="collection-card">
                  {item.frontImageUrl ? (
                    <img src={item.frontImageUrl} alt={item.displayName || item.cardNameCanonical} />
                  ) : (
                    <div className="collection-card-placeholder">No image</div>
                  )}
                  <div className="collection-card-body">
                    <h3>{item.displayName || item.cardNameCanonical}</h3>
                    <p className="collection-meta-strong">{`Quantity: ${item.quantity || 1}`}</p>
                    <p>{[canonicalGameToDisplayLabel(item.cardGame), item.condition].filter(Boolean).join(" • ") || "Unspecified"}</p>
                    {item.gradingCompany && item.gradingCompany !== "raw" && (
                      <p>{`${String(item.gradingCompany).toUpperCase()} ${item.gradeNumber || ""}`.trim()}</p>
                    )}
                    {item.scanAnalysis?.analysis?.authenticityRisk?.level && (
                      <span className="collection-risk-tag">
                        Authenticity: {item.scanAnalysis.analysis.authenticityRisk.level}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn-primary collection-trade-btn"
                      onClick={() => void handleTradeFromCollection(item)}
                      disabled={tradingId === item.id}
                    >
                      {tradingId === item.id ? "Preparing..." : "Trade"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
